import {
  DirectoriesBatchCreateModel,
  DirectoryBatchCreateItem,
  DirectoryBatchCreatedItem,
} from './directoryModel';
import {
  PostDirectoriesBatchItemOutDto,
  PostDirectoriesBatchOutDto,
} from 'shared/dtos/out/directory';
import { DirectoryBatchInDto } from 'shared/dtos/in/directory';

export type DirectoryTreeNode = {
  name: string;
  path: string;
  children: Map<string, DirectoryTreeNode>;
};

export function buildDirectoryTree(paths: string[]): DirectoryTreeNode[] {
  const root = new Map<string, DirectoryTreeNode>();

  paths.forEach((path) => {
    if (!path) {
      return;
    }

    const segments = path.split('/').filter(Boolean);
    let currentMap = root;
    let currentPath = '';

    segments.forEach((segment) => {
      const nextPath = currentPath ? `${currentPath}/${segment}` : segment;

      if (!currentMap.has(segment)) {
        currentMap.set(segment, {
          name: segment,
          path: nextPath,
          children: new Map<string, DirectoryTreeNode>(),
        });
      }

      const treeNode = currentMap.get(segment);
      if (!treeNode) {
        throw new Error(
          `Expected tree node for segment "${segment}" at path "${nextPath}" to exist, but it was not found in the map. This indicates a logic error in buildDirectoryTree.`
        );
      }
      currentMap = treeNode.children;
      currentPath = nextPath;
    });
  });

  return Array.from(root.values());
}

export function directoryTreeNodeToBatchCreateModel(
  treeNode: DirectoryTreeNode
): DirectoryBatchCreateItem {
  const model: DirectoryBatchCreateItem = {
    name: treeNode.name,
    clientPath: treeNode.path,
  };

  if (treeNode.children.size) {
    model.directories = Array.from(treeNode.children.values()).map(
      (child) => directoryTreeNodeToBatchCreateModel(child)
    );
  }

  return model;
}

export function directoryTreeNodeToBatchCreateModelWithParent(
  treeNode: DirectoryTreeNode,
  isTopLevel: boolean,
  parentDirectoryId: string | null
): DirectoryBatchCreateItem {
  const model: DirectoryBatchCreateItem = {
    name: treeNode.name,
    clientPath: treeNode.path,
  };

  if (isTopLevel) {
    if (parentDirectoryId) {
      model.parentId = parentDirectoryId;
    } else {
      model.isRootDirectory = true;
    }
  }

  if (treeNode.children.size) {
    model.directories = Array.from(treeNode.children.values()).map(
      (child) => directoryTreeNodeToBatchCreateModel(child)
    );
  }

  return model;
}

export function buildDirectoriesBatchCreateModel(
  paths: string[],
  parentDirectoryId: string | null
): DirectoriesBatchCreateModel {
  const filteredPaths = paths.filter(Boolean);
  if (!filteredPaths.length) {
    return {
      directories: [],
    };
  }

  const tree = buildDirectoryTree(filteredPaths);
  return {
    directories: tree.map((treeNode) =>
      directoryTreeNodeToBatchCreateModelWithParent(
        treeNode,
        true,
        parentDirectoryId
      )
    ),
  };
}

// ============================================================================
// Batch API mapping types and functions
// ============================================================================

const ROOT_PARENT_KEY = '__ROOT__';

export type NestedDirectoryBatchInDto = DirectoryBatchInDto & {
  directories?: NestedDirectoryBatchInDto[];
};

export type FlattenedBatchNode = {
  name: string;
  path: string;
  parentId?: string;
  isRootDirectory?: boolean;
};

export type NormalizedBatchRequest = {
  requestBody: PostDirectoriesBatchOutDto;
  flatNodes: FlattenedBatchNode[];
};

export function flattenNestedDirectories(
  directories: NestedDirectoryBatchInDto[]
): DirectoryBatchInDto[] {
  const result: DirectoryBatchInDto[] = [];

  function flatten(dirs: NestedDirectoryBatchInDto[]): void {
    for (const dir of dirs) {
      const { directories: children, ...rest } = dir;
      result.push(rest);
      if (children?.length) {
        flatten(children);
      }
    }
  }

  flatten(directories);
  return result;
}

export function normalizeBatchRequest(
  model: DirectoriesBatchCreateModel
): NormalizedBatchRequest {
  if (!model.directories.length) {
    throw new Error('Directories payload cannot be empty.');
  }

  const flatNodes: FlattenedBatchNode[] = [];

  function mapChild(
    child: DirectoryBatchCreateItem,
    parentPath: string
  ): PostDirectoriesBatchItemOutDto {
    if (child.parentId) {
      throw new Error('Nested directories cannot provide parentId.');
    }
    if (child.isRootDirectory) {
      throw new Error('Only root directories can set isRootDirectory.');
    }
    const path = `${parentPath}/${child.name}`;
    flatNodes.push({
      name: child.name,
      path,
    });
    return {
      name: child.name,
      directories: child.directories
        ? child.directories.map((nested) => mapChild(nested, path))
        : undefined,
    };
  }

  function mapItem(
    item: DirectoryBatchCreateItem,
    parentPath: string
  ): PostDirectoriesBatchItemOutDto {
    validateDirectoryItem(item, parentPath);
    const path = parentPath ? `${parentPath}/${item.name}` : item.name;
    flatNodes.push({
      name: item.name,
      path,
      parentId: item.parentId,
      isRootDirectory: item.isRootDirectory,
    });
    return {
      name: item.name,
      parentId: item.parentId,
      isRootDirectory: item.isRootDirectory,
      directories: item.directories
        ? item.directories.map((child) => mapChild(child, path))
        : undefined,
    };
  }

  const directories = model.directories.map((directory) =>
    mapItem(directory, '')
  );

  return {
    requestBody: { directories },
    flatNodes,
  };
}

function validateDirectoryItem(
  item: DirectoryBatchCreateItem,
  parentPath: string
): void {
  if (!item.name.trim()) {
    throw new Error('Directory name cannot be empty.');
  }

  const isTopLevel = parentPath === '';
  const isNested = !isTopLevel;
  const hasParentId = Boolean(item.parentId);
  const hasRootFlag = Boolean(item.isRootDirectory);

  if (isTopLevel) {
    if (!hasParentId && !hasRootFlag) {
      throw new Error(
        'Top-level directory must specify either parentId or isRootDirectory flag.'
      );
    }
    if (hasParentId && hasRootFlag) {
      throw new Error(
        'Top-level directory cannot have both parentId and isRootDirectory flag.'
      );
    }
  }

  if (isNested) {
    if (hasParentId) {
      throw new Error(
        `Nested directory "${item.name}" cannot specify parentId.`
      );
    }
    if (hasRootFlag) {
      throw new Error(
        `Nested directory "${item.name}" cannot be marked as root directory.`
      );
    }
  }
}

export function enforceRequestUniqueness(nodes: FlattenedBatchNode[]): void {
  const seenByParent = new Map<string, Set<string>>();

  nodes.forEach((node) => {
    const parentKey = node.path.split('/').slice(0, -1).join('/');
    const key = parentKey || ROOT_PARENT_KEY;
    if (!seenByParent.has(key)) {
      seenByParent.set(key, new Set());
    }
    const seenSet = seenByParent.get(key)!;
    const lowerName = node.name.toLocaleLowerCase();
    if (seenSet.has(lowerName)) {
      throw new Error(
        `Duplicate folder "${node.name}" in the same parent directory.`
      );
    }
    seenSet.add(lowerName);
  });
}

export function mapResponseToCreatedItems(
  flatDirectories: DirectoryBatchInDto[],
  flatNodes: FlattenedBatchNode[]
): DirectoryBatchCreatedItem[] {
  const flatNodeLookup = new Map<string, Map<string, FlattenedBatchNode>>();
  for (const node of flatNodes) {
    const parentKey = node.parentId ?? (node.isRootDirectory ? ROOT_PARENT_KEY : '');
    if (!flatNodeLookup.has(parentKey)) {
      flatNodeLookup.set(parentKey, new Map());
    }
    flatNodeLookup.get(parentKey)!.set(node.name.toLowerCase(), node);
  }

  const idToPath = new Map<string, string>();

  return flatDirectories.map((directory) => {
    const dirParentId = directory.parentId ?? '';
    const dirName = directory.name?.toLowerCase() ?? '';

    let matchedNode: FlattenedBatchNode | undefined;

    if (flatNodeLookup.has(dirParentId)) {
      matchedNode = flatNodeLookup.get(dirParentId)?.get(dirName);
    }

    if (!matchedNode && !dirParentId) {
      matchedNode = flatNodeLookup.get(ROOT_PARENT_KEY)?.get(dirName);
    }

    if (!matchedNode && dirParentId && idToPath.has(dirParentId)) {
      const parentPath = idToPath.get(dirParentId)!;
      const expectedPath = `${parentPath}/${directory.name}`;
      matchedNode = flatNodes.find(
        (n) => n.path.toLowerCase() === expectedPath.toLowerCase()
      );
    }

    const path = matchedNode?.path ?? directory.path ?? directory._id;

    if (directory._id && path) {
      idToPath.set(directory._id, path);
    }

    return {
      path,
      _id: directory._id,
      parentId: directory.parentId ?? matchedNode?.parentId,
    };
  });
}
